import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useApi } from '@/hooks/use-api'
import { useLimitsPlan } from '@/hooks/use-plan'
import { useLeads } from '@/features/leads/use-leads'
import { SequenceBuilder } from './sequence-builder'
import { useCreateCampaign, useActivateCampaign } from './use-campaigns'
import type { CreateCampaignInput, SequenceConfig, SequenceStep, StyleSamplesMode } from './types'
import { maxLeadsPerCampaignFor, campaignCapMessage, type OfferConfig } from '@dealcloser/shared'

type IcpConfig = {
  readonly jobTitles: readonly string[]
  readonly industries: readonly string[]
  readonly companySizes: readonly string[]
  readonly locations: readonly string[]
  readonly industryKeywords: string
  readonly competitorUrls: readonly string[]
}

const TOTAL_STEPS = 5

const STEP_TYPE_LABELS: Record<string, string> = {
  connection_request: 'Connection Request',
  first_dm: 'First DM',
  follow_up_1: 'Follow-up 1',
  follow_up_2: 'Follow-up 2',
  follow_up_3: 'Follow-up 3',
}

// Full 5-step cadence by default. Previously this was 3 steps (capped at
// follow_up_1), which meant getNextStepFromSequence() returned 'none' for
// any lead that reached followup_1_sent — so follow-ups 2 and 3 never fired
// and the kanban columns stayed empty. Matches the Pro-plan default in
// v3/workers/campaigns/src/routes/sequence.ts.
const DEFAULT_SEQUENCE_STEPS: readonly SequenceStep[] = [
  { type: 'connection_request', delay_days: 0, strategy: 'curiosity_gap' },
  { type: 'first_dm', delay_days: 1, strategy: 'curiosity_gap' },
  { type: 'follow_up_1', delay_days: 2, strategy: 'pain_agitate' },
  { type: 'follow_up_2', delay_days: 4, strategy: 'mutual_value' },
  { type: 'follow_up_3', delay_days: 7, strategy: 'social_proof' },
]

const DEFAULT_SEQUENCE_CONFIG: SequenceConfig = {
  steps: DEFAULT_SEQUENCE_STEPS,
  daily_limit: 30,
  activity_window: { start_hour: 9, end_hour: 17, timezone: 'America/New_York' },
  ramp_up: true,
}

type WizardState = {
  name: string
  daily_limit: number
  lead_ids: readonly string[]
  sequence_steps: readonly SequenceStep[]
  target_persona: string
  /** How to apply user style samples — global (default) or per_step. */
  style_samples_mode: StyleSamplesMode
  /** Sample messages used when style_samples_mode === 'global'. */
  style_samples_global: readonly string[]
  /** Whether to flip the new campaign from draft → active right after creation. */
  activate_now: boolean
}

const INITIAL_STATE: WizardState = {
  name: '',
  daily_limit: 30,
  lead_ids: [],
  sequence_steps: DEFAULT_SEQUENCE_STEPS,
  target_persona: '',
  style_samples_mode: 'global',
  style_samples_global: [],
  activate_now: false,
}

type StepIndicatorProps = {
  readonly currentStep: number
}

function StepIndicator({ currentStep }: StepIndicatorProps) {
  const steps = [
    { label: 'Details' },
    { label: 'Leads' },
    { label: 'Sequence' },
    { label: 'Preview' },
    { label: 'Review' },
  ]

  return (
    <div className="flex items-center gap-0">
      {steps.map((step, index) => {
        const stepNumber = index + 1
        const isCompleted = stepNumber < currentStep
        const isCurrent = stepNumber === currentStep

        return (
          <div key={step.label} className="flex items-center">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                isCompleted
                  ? 'bg-primary text-primary-foreground'
                  : isCurrent
                    ? 'border-2 border-primary text-primary'
                    : 'border-2 border-muted text-muted-foreground'
              }`}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" /> : stepNumber}
            </div>
            <span
              className={`ml-1.5 text-xs ${isCurrent ? 'font-medium' : 'text-muted-foreground'}`}
            >
              {step.label}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`mx-3 h-px w-8 ${stepNumber < currentStep ? 'bg-primary' : 'bg-border'}`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

type Step1Props = {
  readonly name: string
  readonly onChange: (name: string) => void
}

// 2026-07-27 owner directive: the per-campaign daily message limit is no
// longer user-configurable or shown anywhere. The platform sets daily sending
// volume from plan tier + LinkedIn safety rules (the wizard still submits the
// standard default so the backend cap machinery is unchanged).
function Step1Details({ name, onChange }: Step1Props) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="campaign-name">Campaign Name</Label>
        <Input
          id="campaign-name"
          placeholder="e.g. Q1 SaaS Founders Outreach"
          value={name}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      <div className="rounded-md border bg-muted/30 px-3 py-2.5">
        <p className="text-sm font-medium">Sending schedule</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Messages go out between 9:00 AM and 5:00 PM New York time, Monday
          through Friday. Weekends are paused unless you switch on weekend
          activity in Settings, Safety. Daily sending volume is managed for
          you within LinkedIn's safe limits.
        </p>
      </div>
    </div>
  )
}

type Step2Props = {
  readonly selectedIds: readonly string[]
  readonly onChange: (ids: readonly string[]) => void
}

function Step2Leads({ selectedIds, onChange }: Step2Props) {
  const { data: leads = [], isLoading } = useLeads()
  const [search, setSearch] = useState('')
  // Default to leads that are NOT already in a campaign, so "Select all"
  // can't quietly pull people out of a running sequence (beta QA feedback).
  // "All leads" stays available for deliberate moves and shows an amber
  // "In: <campaign>" badge on every assigned lead.
  const [scope, setScope] = useState<'available' | 'all'>('available')

  // Per-plan campaign cap (Basic 30 / Pro 60; comp + agency unlimited). A new
  // campaign starts empty, so the number of selectable leads == the cap. The
  // backend trims anything beyond this too — clamping here just keeps the
  // selection honest. Cap math uses the comp-aware limits plan: usePlan()
  // routes comp accounts to 'pro' for UI gating, but their server-side cap is
  // Agency-unlimited, so clamping at 60 here blocked beta testers for nothing.
  const plan = useLimitsPlan()
  const perCampaignCap = maxLeadsPerCampaignFor(plan)
  const capReached = Number.isFinite(perCampaignCap) && selectedIds.length >= perCampaignCap

  const availableLeads = leads.filter((lead) => !lead.campaign_id)
  const scoped = scope === 'available' ? availableLeads : leads

  const filtered = search.trim()
    ? scoped.filter((lead) => {
        const q = search.toLowerCase()
        const name = (lead.full_name ?? `${lead.first_name} ${lead.last_name}`).toLowerCase()
        const title = (lead.job_title ?? '').toLowerCase()
        const company = (lead.company ?? '').toLowerCase()
        return name.includes(q) || title.includes(q) || company.includes(q)
      })
    : scoped

  const selectedFromOtherCampaigns = leads.filter(
    (lead) => lead.campaign_id && selectedIds.includes(lead.id),
  ).length

  function toggleLead(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((lid) => lid !== id))
    } else {
      // Respect the per-plan cap — ignore adds once we're full.
      if (Number.isFinite(perCampaignCap) && selectedIds.length >= perCampaignCap) return
      onChange([...selectedIds, id])
    }
  }

  function toggleAll(checked: boolean) {
    if (checked) {
      const allIds = filtered.map((l) => l.id)
      let merged = [...new Set([...selectedIds, ...allIds])]
      if (Number.isFinite(perCampaignCap) && merged.length > perCampaignCap) {
        merged = merged.slice(0, perCampaignCap)
      }
      onChange(merged)
    } else {
      const filteredIds = new Set(filtered.map((l) => l.id))
      onChange(selectedIds.filter((id) => !filteredIds.has(id)))
    }
  }

  const allFilteredSelected = filtered.length > 0 && filtered.every((l) => selectedIds.includes(l.id))

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Loading leads...</div>
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Select leads to add to this campaign.
        </p>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={() => onChange([])}
            >
              Clear selection
            </Button>
          )}
          <Badge variant="secondary">
            {selectedIds.length}
            {Number.isFinite(perCampaignCap) ? ` / ${perCampaignCap}` : ''} selected
          </Badge>
        </div>
      </div>

      {/* Scope toggle: keep already-campaigned leads out of the picker by
          default so nobody gets double-sequenced by accident. */}
      <div className="flex items-center gap-1">
        <Button
          variant={scope === 'available' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope('available')}
        >
          Not in a campaign ({availableLeads.length})
        </Button>
        <Button
          variant={scope === 'all' ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => setScope('all')}
        >
          All leads ({leads.length})
        </Button>
      </div>

      {capReached && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {campaignCapMessage(plan)}
        </p>
      )}

      {selectedFromOtherCampaigns > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            {selectedFromOtherCampaigns === 1
              ? '1 selected lead is already in another campaign and will be moved into this new one.'
              : `${selectedFromOtherCampaigns} selected leads are already in other campaigns and will be moved into this new one.`}
          </span>
        </div>
      )}

      {leads.length > 5 && (
        <Input
          placeholder="Search leads..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
      )}

      <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
        {leads.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No leads found. Add leads first.
          </div>
        )}
        {leads.length > 0 && scoped.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Every lead is already in a campaign. Switch to "All leads" to move
            leads out of their current campaign.
          </div>
        )}
        {filtered.length > 0 && (
          <div
            className="flex items-center gap-3 px-3 py-2 bg-muted/30 cursor-pointer sticky top-0 z-10 border-b"
            onClick={() => toggleAll(!allFilteredSelected)}
          >
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={(checked) => toggleAll(!!checked)}
              aria-label="Select all leads"
            />
            <span className="text-sm font-medium text-muted-foreground">
              Select all{search.trim() ? ` (${filtered.length} matching)` : ` (${filtered.length})`}
            </span>
          </div>
        )}
        {filtered.map((lead) => {
          const displayName = lead.full_name ?? `${lead.first_name} ${lead.last_name}`
          const isSelected = selectedIds.includes(lead.id)

          return (
            <div
              key={lead.id}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 cursor-pointer"
              onClick={() => toggleLead(lead.id)}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleLead(lead.id)}
                aria-label={`Select ${displayName}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{displayName}</p>
                  {lead.campaign_id && (
                    <Badge
                      variant="outline"
                      className="shrink-0 text-[10px] px-1.5 py-0 h-4 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                    >
                      In: {lead.campaign ?? 'another campaign'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {lead.job_title ?? 'Unknown role'} &middot; {lead.company ?? 'Unknown company'}
                </p>
              </div>
            </div>
          )
        })}
        {search.trim() && filtered.length === 0 && scoped.length > 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No leads match "{search}"
          </div>
        )}
      </div>
    </div>
  )
}

type Step3Props = {
  readonly steps: readonly SequenceStep[]
  readonly onChange: (steps: readonly SequenceStep[]) => void
  readonly styleSamplesMode: StyleSamplesMode
  readonly onStyleSamplesModeChange: (mode: StyleSamplesMode) => void
  readonly styleSamplesGlobal: readonly string[]
  readonly onStyleSamplesGlobalChange: (samples: readonly string[]) => void
}

function Step3Sequence({
  steps,
  onChange,
  styleSamplesMode,
  onStyleSamplesModeChange,
  styleSamplesGlobal,
  onStyleSamplesGlobalChange,
}: Step3Props) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        Configure the outreach sequence for this campaign.
      </p>
      <SequenceBuilder
        steps={steps}
        onChange={onChange}
        styleSamplesMode={styleSamplesMode}
        onStyleSamplesModeChange={onStyleSamplesModeChange}
        styleSamplesGlobal={styleSamplesGlobal}
        onStyleSamplesGlobalChange={onStyleSamplesGlobalChange}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4 — AI output preview (TJ's ask, 2026-07-14): after the user pastes
// their sample messages in the Sequence step, "Next" shows what the AI's
// messages would look like BEFORE the campaign exists or anything sends.
// Generated against one generic placeholder prospect on the FREE model
// (the backend forces plan:'starter'), so previewing and regenerating are
// $0 on every plan. Server caches by (steps, samples, offer) for an hour —
// bouncing back and forth between steps doesn't regenerate.
// ---------------------------------------------------------------------------

type GenericPreviewStepResult = {
  readonly type: string
  readonly strategy: string
  readonly message: string | null
  readonly error?: string
  readonly no_body?: boolean
}

type GenericPreviewData = {
  readonly steps: readonly GenericPreviewStepResult[]
  readonly generated_at: string
  readonly cached: boolean
}

type StepAIPreviewProps = {
  readonly sequenceSteps: readonly SequenceStep[]
  readonly styleSamples: readonly string[]
}

function StepAIPreview({ sequenceSteps, styleSamples }: StepAIPreviewProps) {
  const apiFetch = useApi()
  const [data, setData] = useState<GenericPreviewData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const firedRef = useRef(false)

  const samples = styleSamples.filter((s) => s.trim().length > 0)

  async function generatePreview(force: boolean) {
    setIsLoading(true)
    setError(null)
    try {
      const res = await apiFetch<GenericPreviewData>('/api/campaigns/preview-generic', {
        method: 'POST',
        body: {
          steps: sequenceSteps.map((s) => ({ type: s.type, strategy: s.strategy })),
          style_samples: samples,
          force,
        },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Preview failed')
      setData(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setIsLoading(false)
    }
  }

  // Auto-generate once when the step mounts. Re-entering the step remounts
  // and re-fires, but the server's cache answers instantly for unchanged
  // inputs — and regenerates only when the user actually edited something.
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    void generatePreview(false)
  }, []) // fire once on mount — firedRef guards StrictMode double-invoke

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-primary" />
            What your outreach will look like
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Draft messages for a sample prospect (Alex Morgan, Operations Manager at Meridian
            Consulting), written from your sequence, offer, and writing samples. Nothing is sent.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void generatePreview(true)}
          disabled={isLoading}
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
          Regenerate
        </Button>
      </div>

      {samples.length === 0 && (
        <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          No writing samples yet. Go <span className="font-semibold">Back</span> and paste up to 3
          messages under &ldquo;Your writing style&rdquo; to make these drafts sound like you.
        </p>
      )}

      {isLoading && !data && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drafting your messages…</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void generatePreview(false)}>
            Try again
          </Button>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-3">
          {data.steps.map((step, index) => (
            <div key={`${step.type}-${index}`} className="rounded-lg border p-3">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {index + 1}
                </span>
                <span className="text-xs font-semibold">
                  {STEP_TYPE_LABELS[step.type] ?? step.type}
                </span>
              </div>
              {step.no_body ? (
                <p className="text-xs italic text-muted-foreground">
                  Plain connection invite, no message attached (deliberate: invites without notes
                  get accepted more).
                </p>
              ) : step.message ? (
                <p className="whitespace-pre-wrap rounded-md bg-muted/40 px-3 py-2 text-sm leading-relaxed">
                  {step.message}
                </p>
              ) : (
                <p className="text-xs text-destructive">
                  Could not draft this step{step.error ? `: ${step.error}` : ''}. Try Regenerate.
                </p>
              )}
            </div>
          ))}
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Instant preview drafts. Live outreach personalizes every message to the specific lead
            (their role, company, and activity), so real messages are typically sharper than
            these.
          </p>
        </div>
      )}
    </div>
  )
}

type Step4Props = {
  readonly state: WizardState
  readonly onActivateNowChange: (value: boolean) => void
}

function Step4Review({ state, onActivateNowChange }: Step4Props) {
  const STRATEGY_LABELS: Record<string, string> = {
    Pain_Agitate: 'Pain Agitate',
    Mutual_Value: 'Mutual Value',
    Social_Proof: 'Social Proof',
    Curiosity_Gap: 'Curiosity Gap',
    Direct_Expert: 'Direct Expert',
    Challenge: 'Challenge',
    Trojan_Horse: 'Trojan Horse',
    Mutual_Connection: 'Mutual Connection',
  }

  // Activation requires at least one lead — backend rejects with 400 otherwise.
  const canActivate = state.lead_ids.length > 0

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border divide-y">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-muted-foreground">Campaign name</span>
          <span className="text-sm font-medium">{state.name || '(unnamed)'}</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-muted-foreground">Leads</span>
          <span className="text-sm font-medium">{state.lead_ids.length} selected</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-muted-foreground">Sending schedule</span>
          <span className="text-sm font-medium">Weekdays, 9 AM to 5 PM ET</span>
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm text-muted-foreground">Sequence steps</span>
          <span className="text-sm font-medium">{state.sequence_steps.length} steps</span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Sequence Preview
        </p>
        <div className="flex flex-col gap-1.5">
          {state.sequence_steps.map((step, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {index + 1}
              </span>
              <span className="font-medium text-foreground">
                {STEP_TYPE_LABELS[step.type] ?? step.type}
              </span>
              <span>&middot;</span>
              <span>{STRATEGY_LABELS[step.strategy] ?? step.strategy}</span>
              {step.delay_days > 0 && (
                <>
                  <span>&middot;</span>
                  <span>+{step.delay_days}d</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-4">
        <p className="text-sm font-medium">Activate this campaign now?</p>
        <p className="text-xs text-muted-foreground">
          {canActivate
            ? 'If yes, outreach starts on the next scheduled run. If no, the campaign is saved as a draft you can activate later.'
            : 'Add at least one lead in Step 2 to enable immediate activation.'}
        </p>
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            variant={state.activate_now ? 'outline' : 'default'}
            className="flex-1"
            onClick={() => onActivateNowChange(false)}
          >
            No, save as draft
          </Button>
          <Button
            type="button"
            variant={state.activate_now ? 'default' : 'outline'}
            className="flex-1"
            disabled={!canActivate}
            onClick={() => onActivateNowChange(true)}
          >
            Yes, activate now
          </Button>
        </div>
      </div>
    </div>
  )
}

type CreateCampaignWizardProps = {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onCreated?: (id: string) => void
}

export function CreateCampaignWizard({
  open,
  onOpenChange,
  onCreated,
}: CreateCampaignWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [state, setState] = useState<WizardState>(INITIAL_STATE)
  const [activationError, setActivationError] = useState<string | null>(null)
  const createCampaign = useCreateCampaign()
  const activateCampaign = useActivateCampaign()
  const apiFetch = useApi()
  // Prevent re-populating after the user has edited a field
  const autoPopulatedRef = useRef(false)

  const { data: icpData } = useQuery({
    queryKey: ['settings', 'icp'],
    queryFn: async () => {
      const res = await apiFetch<IcpConfig>('/api/settings/icp')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  const { data: offerData } = useQuery({
    queryKey: ['settings', 'offer'],
    queryFn: async () => {
      const res = await apiFetch<OfferConfig>('/api/settings/offer')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
  })

  // Auto-populate name and target_persona from settings when the wizard opens,
  // but only if the user hasn't typed anything yet.
  useEffect(() => {
    if (!open) return
    if (autoPopulatedRef.current) return
    if (!icpData && !offerData) return

    autoPopulatedRef.current = true

    setState((prev) => {
      // Build campaign name from ICP job titles or offer target audience
      let suggestedName = prev.name
      if (!suggestedName.trim()) {
        if (icpData?.jobTitles && icpData.jobTitles.length > 0) {
          suggestedName = `${icpData.jobTitles[0]} Outreach`
        } else if (offerData?.targetAudience) {
          suggestedName = `${offerData.targetAudience} Campaign`
        }
      }

      // Build target_persona from ICP
      let suggestedPersona = prev.target_persona
      if (!suggestedPersona.trim() && icpData) {
        const parts: string[] = []
        if (icpData.jobTitles.length > 0) parts.push(icpData.jobTitles.join(', '))
        if (icpData.industries.length > 0) parts.push(icpData.industries.join(', '))
        suggestedPersona = parts.join(' | ')
      }

      return { ...prev, name: suggestedName, target_persona: suggestedPersona }
    })
  }, [open, icpData, offerData])

  function resetWizard() {
    setCurrentStep(1)
    setState(INITIAL_STATE)
    setActivationError(null)
    autoPopulatedRef.current = false
  }

  function handleClose(value: boolean) {
    if (!value) resetWizard()
    onOpenChange(value)
  }

  function canAdvance(): boolean {
    if (currentStep === 1) return state.name.trim().length > 0
    if (currentStep === 2) return true // leads optional
    if (currentStep === 3) return state.sequence_steps.length > 0
    return true
  }

  function handleNext() {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((s) => s + 1)
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1)
    }
  }

  async function handleCreate() {
    setActivationError(null)
    const input: CreateCampaignInput = {
      name: state.name,
      daily_limit: state.daily_limit,
      lead_ids: state.lead_ids,
      sequence_config: {
        ...DEFAULT_SEQUENCE_CONFIG,
        steps: state.sequence_steps,
        daily_limit: state.daily_limit,
        style_samples_mode: state.style_samples_mode,
        style_samples_global: state.style_samples_global.filter((s) => s.trim().length > 0),
      },
      target_persona: state.target_persona || null,
    }

    const result = await createCampaign.mutateAsync(input)

    if (state.activate_now && state.lead_ids.length > 0) {
      try {
        await activateCampaign.mutateAsync(result.id)
      } catch (err) {
        // Campaign was created as draft but activation failed (e.g. LinkedIn
        // not connected). Keep the dialog open with an inline error so the
        // user knows the campaign exists but is still in draft.
        setActivationError(
          err instanceof Error
            ? err.message
            : 'Could not activate campaign. It was saved as a draft. Activate it from the campaign page.',
        )
        return
      }
    }

    resetWizard()
    onOpenChange(false)
    onCreated?.(result.id)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Create Campaign</DialogTitle>
          <DialogDescription>
            Set up a new outreach campaign in a few steps.
          </DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="border-b pb-4 shrink-0">
          <StepIndicator currentStep={currentStep} />
        </div>

        {/* Step content */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {currentStep === 1 && (
            <Step1Details
              name={state.name}
              onChange={(name) => setState((s) => ({ ...s, name }))}
            />
          )}
          {currentStep === 2 && (
            <Step2Leads
              selectedIds={state.lead_ids}
              onChange={(lead_ids) => setState((s) => ({ ...s, lead_ids }))}
            />
          )}
          {currentStep === 3 && (
            <Step3Sequence
              steps={state.sequence_steps}
              onChange={(sequence_steps) =>
                setState((s) => ({ ...s, sequence_steps }))
              }
              styleSamplesMode={state.style_samples_mode}
              onStyleSamplesModeChange={(style_samples_mode) =>
                setState((s) => ({ ...s, style_samples_mode }))
              }
              styleSamplesGlobal={state.style_samples_global}
              onStyleSamplesGlobalChange={(style_samples_global) =>
                setState((s) => ({ ...s, style_samples_global }))
              }
            />
          )}
          {currentStep === 4 && (
            <StepAIPreview
              sequenceSteps={state.sequence_steps}
              styleSamples={state.style_samples_global}
            />
          )}
          {currentStep === 5 && (
            <Step4Review
              state={state}
              onActivateNowChange={(activate_now) =>
                setState((s) => ({ ...s, activate_now }))
              }
            />
          )}
          {currentStep === 5 && activationError && (
            <p className="mt-3 text-sm text-destructive">{activationError}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0 border-t pt-4">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="mr-auto"
          >
            Cancel
          </Button>
          {currentStep < TOTAL_STEPS ? (
            <Button onClick={handleNext} disabled={!canAdvance()}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={
                createCampaign.isPending ||
                activateCampaign.isPending ||
                !state.name.trim()
              }
            >
              {createCampaign.isPending
                ? 'Creating...'
                : activateCampaign.isPending
                  ? 'Activating...'
                  : state.activate_now && state.lead_ids.length > 0
                    ? 'Create & Activate'
                    : 'Create Campaign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
